import { Row, Col } from "antd";

import styles from "./styles.module.scss";
import { useResponsiveQuery } from "utils";
import { getDefaultCookiePolicyStorage } from "../cookiePolicy/policyData";

/**
 * Turns plain-text email addresses in the (whitelabel) policy HTML into blue
 * mailto links. Skips emails already inside an <a> / a mailto: href so we
 * never double-link the ones that are already hyperlinked (#1340).
 */
const linkifyEmails = (html: string): string =>
	html.replace(
		// eslint-disable-next-line no-useless-escape
		/(?<!mailto:)(?<!["'])\b([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})\b(?![^<]*?<\/a>)/g,
		'<a href="mailto:$1" style="color: #1677ff;">$1</a>',
	);

const PrivacyPolicy = ({
	data,
	communityName,
	paymentsEnabled = false,
}: {
	data?: any;
	communityName?: string;
	paymentsEnabled?: boolean;
}) => {
	const { isMobile } = useResponsiveQuery();
	const defaultStorage = getDefaultCookiePolicyStorage(paymentsEnabled);

	return (
		<div className={styles.content}>
			<div className={styles.title}>
				{communityName ? communityName : "UGENIE"}
			</div>
			<div className={styles.subTitle}>PLATFORM PRIVACY POLICY</div>
			{data ? (
				<Row className={styles.row}>
					<Col span={24} className={styles.normal}>
						<div
							dangerouslySetInnerHTML={{
								__html: linkifyEmails(data),
							}}
						/>
					</Col>
				</Row>
			) : (
				<>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.bold}>
							Effective Date: 25 June 2025
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							1
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							Introduction
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							1.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie Ltd ("Ugenie", "we", "us" or "our") is
							committed to safeguarding the privacy of all users
							of our Platform, which includes our website (
							<a href="mailto:www.ugenie.io">www.ugenie.io</a>),
							mobile applications, and any white-labelled
							subdomains or branded community platforms operated
							on behalf of our customers (collectively, the
							"Platform").
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							1.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							We process personal data in accordance with the UK
							General Data Protection Regulation (UK GDPR), the
							Data Protection Act 2018 (DPA 2018), and other
							applicable laws. This Privacy and Cookie Policy
							("Policy") sets out how we collect, use, disclose
							and protect your personal data.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							1.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							This Policy is designed to be transparent and easy
							to understand, outlining:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							What data we collect;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							How we use and share it;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							How we safeguard your information;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Your rights and choices regarding your personal
							data.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							1.4
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							By continuing to use our Platform, you agree to this
							Policy and any updates made to it.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							1.5
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							In this Policy:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							"Ugenie", "we", "us" and "our" refer to Ugenie Ltd;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							"User", "you" or "your" means the individual who
							accesses or uses the Platform;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							"Platform" refers to all websites, applications, and
							white-labelled solutions provided by Ugenie;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							"Community" refers to a group hosted on the
							Platform, typically managed by a customer
							organisation;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							"Customer" means the organisation that has
							contracted Ugenie for a white-labelled solution.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							2
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							SCOPE OF THIS POLICY
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							2.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							This Policy applies to personal data collected and
							processed by Ugenie in connection with:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							The operation of the Ugenie Platform and our related
							services;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Use of our website, mobile app, and any associated
							white-labelled portals;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Communications with prospective and existing users,
							customers, or partners.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							2.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie provides its services to customers via a
							white-labelling model. Depending on the situation,
							we act in one of the following roles:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							As a <strong>Data Controller</strong> when we
							determine the purpose and means of processing
							personal data (e.g., for marketing, analytics, or
							direct account relationships);
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							As a <strong>Data Processor</strong> when we act on
							behalf of our customers (who are Data Controllers)
							by managing personal data within their communities
							hosted on the Platform.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							2.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Where Ugenie acts as a Data Processor, we process
							personal data only in accordance with our customer’s
							instructions, and as necessary to deliver and
							maintain the Platform securely and effectively.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							2.4
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Where this Policy refers to rights or
							responsibilities, those apply differently depending
							on whether Ugenie is acting as a Data Controller or
							Data Processor. This distinction is further
							explained throughout this document.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							3
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							CATEGORIES OF PERSONAL DATA COLLECTED
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							3.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							We collect and process the following categories of
							personal data:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Usage Data</strong> – Information about how
							you use our website, app, and services, such as IP
							address, browser details, pages visited, and device
							metadata.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Account Data</strong> – Details you provide
							when creating or maintaining an account, including
							your name, email address, and contact preferences.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Profile Data</strong> – Optional personal
							information you choose to include in your profile,
							such as your photo, interests, bio, and community
							details.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Service Data</strong> – Information provided
							in connection with the use of services, including
							addresses, phone numbers, and payment details.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Publication Data</strong> – Any data you
							publish on the Platform, including posts, comments,
							or media shared with a Community.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Enquiry Data</strong> – Information you
							submit when contacting us with questions, feedback,
							or support requests.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Transaction Data</strong> – Payment and
							billing information used to process purchases,
							refunds, or subscriptions.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Legal/Administrative Data</strong> – Records
							and documentation maintained for compliance, dispute
							resolution, or lawful administrative purposes.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							3.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							A detailed explanation of each category, purpose and
							legal basis is provided in Section 4.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							4
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							PURPOSES OF PROCESSING AND LEGAL BASIS
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							4.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie collects and processes various categories of
							personal data depending on how you interact with our
							Platform. The following outlines the details of the
							data we collect, why we collect it, and our lawful
							basis under applicable data protection law.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Usage Data</strong>: We collect data on how
							the Platform is accessed and used, including IP
							address, browser type, operating system, referral
							source, pages visited, and time spent. This is used
							to improve platform stability, monitor usage
							patterns, and optimise services.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Legitimate Interests (monitoring and
							improving services) or Consent (where required for
							analytics).
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Account Data</strong>: This includes your
							name, email address, and postal address, and is
							collected when you register or maintain an account.
							This data is used to provide access to the Platform
							and communicate service-related information.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Contractual Necessity or Legitimate
							Interests.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Profile Data</strong>: This includes any
							optional information you include in your profile
							such as your photo, interests, or community
							information. It enables community engagement and
							personalisation features.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Legitimate Interests or Consent.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Service Data</strong>: Includes contact
							information, phone numbers, addresses, and payment
							details provided as part of using Ugenie’s services.
							Used to deliver services, ensure secure operations,
							and facilitate support.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Legitimate Interests or Consent.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Publication Data</strong>: This covers
							content you choose to post on the Platform,
							including text, media, and profile contributions,
							for public or member viewing. Used to facilitate
							community participation.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Contractual Necessity (community
							participation) or Consent (for optional sharing of
							information).
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Enquiry Data</strong>: When you submit a
							contact or support request, we collect relevant
							details to respond effectively and provide marketing
							if opted in.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Legitimate Interests or Consent.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Transaction Data</strong>: We process your
							payment details and transaction history to manage
							billing, refunds, and maintain financial records.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Contractual Necessity or Legal
							Obligation.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Legal/Administrative Data</strong>: This
							includes records required to comply with legal
							obligations, support audits, or manage disputes.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dotOutline} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Legal Basis: Legitimate Interests or Legal
							Obligation.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							4.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							If you supply personal data about someone else
							(e.g., a colleague), you must ensure you have the
							authority and have informed them of this Policy.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							5
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							DATA SHARING
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							5.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							We may share your personal data with trusted third
							parties where necessary for the operation of the
							Platform and our services. These include:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>AWS</strong> - for secure hosting and cloud
							infrastructure;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Google (Firebase)</strong> - for app
							infrastructure, authentication, messaging and
							related platform services;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>GitHub</strong> - for software source
							control and platform delivery services;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Creatella Ventures</strong> - for product
							development, platform operations and support
							services.
						</Col>
					</Row>

					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							5.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie ensures that all subprocessors and external
							service providers only process your personal data
							under strict contractual terms consistent with UK
							GDPR obligations, and solely for the purposes
							outlined in this Policy.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							5.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							In the event we conduct identity or verification
							checks, we may share data with external agencies who
							may retain a record of the enquiry for audit
							purposes.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							6
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							WHITE-LABELLED PLATFORMS
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							6.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie operates a white-labelled platform model,
							providing infrastructure and tools to organisations
							who manage their own communities. In these
							instances:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							The organisation (our customer) acts as the{" "}
							<strong>Data Controller</strong>;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie acts as a <strong>Data Processor</strong>,
							handling data strictly on the controller’s behalf;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie ensures secure storage, platform
							functionality, and data availability, but does not
							determine how community data is used.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							6.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Your community administrator may have additional
							privacy policies or terms governing their use of
							your data. These may apply in conjunction with this
							Policy.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							6.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Where Ugenie processes infrastructure or analytics
							data (such as cookies), this remains governed by
							this Policy.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							6.4
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							For data rights requests related to your community,
							please contact your community administrator
							directly. Ugenie will support them in addressing
							your request as required by law.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							7
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							INTERNATIONAL TRANSFERS
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							7.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie does not currently transfer your personal
							data outside the United Kingdom or the European
							Economic Area (EEA).
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							7.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							If such transfers become necessary (e.g., due to
							future technology or service changes), we will
							ensure your data remains protected through one of
							the following mechanisms:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							A UK Government adequacy regulation or EU Commission
							adequacy decision;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Standard Contractual Clauses (SCCs) approved for
							international data transfers;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Binding corporate rules or other appropriate legal
							safeguards.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							7.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							You may request further information about
							international data transfers by contacting us at{" "}
							<a href="mailto:info@ugenie.io">info@ugenie.io</a>.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							8
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							RETENTION PERIODS
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							8.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Ugenie retains personal data only for as long as
							necessary to fulfil the purposes for which it was
							collected, including to satisfy legal, accounting,
							or reporting requirements.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							8.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Specific retention periods include:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Account data</strong>: up to 7 years
							following the end of your use of the Platform or
							your last activity;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.bold}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Marketing data</strong>: retained until you
							withdraw your consent or unsubscribe;
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.bold}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Transactional and legal data</strong>:
							retained for the duration required under tax,
							financial, or legal compliance laws.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							8.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							In some cases, we may anonymise your personal data
							so that it can no longer be associated with you. We
							may use such anonymised data indefinitely for
							research or statistical purposes.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							8.4
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Data may be retained longer where necessary to
							establish, exercise or defend legal claims.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							9
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							DATA SUBJECT RIGHTS
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							9.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							You have the following rights under the UK General
							Data Protection Regulation (UK GDPR) and the Data
							Protection Act 2018:
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right of Access</strong> - You may request
							confirmation as to whether or not we process your
							personal data, and, where we do, access to that
							personal data and additional details, including the
							purposes of processing and categories of data
							concerned.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Rectification</strong> - You may
							request that inaccurate or incomplete personal data
							be corrected or completed.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Erasure</strong> - In certain
							circumstances, you may request that we delete your
							personal data without undue delay, for example, if
							the data is no longer necessary, you withdraw
							consent, or you object to the processing.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Restrict Processing</strong> - You
							may request the restriction of processing under
							specific circumstances, such as while a dispute over
							accuracy or legal grounds is being resolved.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Object</strong> - You may object to
							processing of your personal data based on legitimate
							interests or for direct marketing. If you object, we
							will cease processing unless there are compelling
							legitimate grounds.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Data Portability</strong> - You may
							request that we provide your personal data to you or
							another organisation in a structured, commonly used,
							machine-readable format, where the processing is
							based on consent or contract and carried out by
							automated means.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Withdraw Consent</strong> - Where
							processing is based on your consent, you may
							withdraw that consent at any time. This does not
							affect the lawfulness of processing carried out
							before the withdrawal.
						</Col>
					</Row>
					<Row
						className={styles.row}
						style={{ marginLeft: isMobile ? 45 : 90 }}
					>
						<Col xs={3} sm={2} className={styles.normal}>
							<div className={styles.dot} />
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Right to Lodge a Complaint</strong> - If you
							believe that our processing of your personal data
							breaches data protection law, you may lodge a
							complaint with the Information Commissioner’s Office
							(ICO) or your local supervisory authority.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							9.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							You can exercise your rights by contacting us at{" "}
							<a href="mailto:info@ugenie.io">info@ugenie.io</a>.
							We may request specific information to confirm your
							identity.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							9.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							If you are using a white-labelled platform, Ugenie
							may act only as the Data Processor. In such cases,
							please direct your request to your Community
							Administrator (the Data Controller). Ugenie will
							support them in fulfilling your request.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							10
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							COOKIES AND TRACKING TECHNOLOGIES
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							10.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							Cookies are small text files stored on your device
							when you visit a website or use an app. They help
							websites operate efficiently and improve your
							experience.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							10.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							We use the following essential cookies and similar
							storage on the Platform:
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col span={24}>
							<div className={styles.tableContainer}>
								<table>
									<tbody>
										<tr>
											<th>Type</th>
											<th>Purpose</th>
											<th>
												Example <br /> Names
											</th>
											<th>Duration</th>
											<th>Legal Basis</th>
										</tr>
										{defaultStorage.map((item) => (
											<tr key={item.category}>
												<td>{item.category}</td>
												<td>{item.purpose}</td>
												<td>{item.names}</td>
												<td>{item.duration}</td>
												<td>Legitimate Interest</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							10.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							We do not currently use analytics or functionality
							cookies. If we introduce them in future, we will
							update this policy and request your explicit consent
							before they are set.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							10.4
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							You can manage your cookie preferences through our
							cookie banner or using your browser settings.
							Essential cookies and authentication storage cannot
							be disabled while you are signed in.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							10.5
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							If you access a white-labelled version of the
							Platform, Ugenie cookies may still be set. These are
							governed by this Policy, while the community owner
							may provide additional terms.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							10.6
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							If you have questions about our use of cookies or
							tracking, please contact{" "}
							<a href="mailto:info@ugenie.io">info@ugenie.io</a>
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							11
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							CHANGES TO THIS POLICY
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							11.1
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							We may update this Policy from time to time to
							reflect changes to our practices, technology, legal
							requirements, or for other operational reasons.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							11.2
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							If we make material changes to this Policy, we will
							notify you via the Platform or email, where
							appropriate. Continued use of the Platform after
							changes indicates your agreement with the updated
							Policy.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal}>
							11.3
						</Col>
						<Col xs={21} sm={22} className={styles.normal}>
							The effective date at the top of this Policy
							reflects the most recent update.
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.bold}>
							12
						</Col>
						<Col xs={21} sm={22} className={styles.bold}>
							CONTACT INFORMATION
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.normal}>
							If you have any questions, concerns, or requests
							regarding this Privacy and Cookie Policy, or our
							data practices more generally, please contact us
							using the details below:
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.normal}>
							<strong>Ugenie Ltd</strong>
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.normal}>
							Company Number: 09966790
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.normal}>
							Registered Office: 5 Gunnersbury Crescent, London,
							W39AA
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.normal}>
							Email:{" "}
							<a href="mailto:info@ugenie.io">info@ugenie.io</a>
						</Col>
					</Row>
					<Row className={styles.row}>
						<Col xs={3} sm={2} className={styles.normal} />
						<Col xs={21} sm={22} className={styles.normal}>
							Website:{" "}
							<a href="https://www.ugenie.io">
								https://www.ugenie.io
							</a>
						</Col>
					</Row>
				</>
			)}
		</div>
	);
};

export default PrivacyPolicy;
